import {
  useMemo,
  useRef,
  type ReactNode,
  Children,
  isValidElement,
  cloneElement,
} from 'react';

import { NUMBER_SPIN_BUTTON_DEFAULTS } from '../../number-spin-button/constants.js';
import { isNumberSpinButton } from '../../number-spin-button/NumberSpinButton.js';
import {
  NumberSpinButtonProps,
  NumberSpinButtonRef,
} from '../../number-spin-button/types.js';
import { isTextSpinButton } from '../../text-spin-button/TextSpinButton.js';
import {
  TextSpinButtonProps,
  TextSpinButtonRef,
} from '../../text-spin-button/types.js';
import { isSpinButtonGroupSeparator } from '../SpinButtonGroupSeparator.js';
import type {
  ChildWithRef,
  SpinButtonGroupSeparatorProps,
  SpinButtonHandler,
} from '../types.js';

/**
 * Handles number spin-button overflow and auto-advance behavior within a group.
 * Applies impossible/complete value focus shifts and forwards trailing overflow when no next target exists.
 */
function handleValueOverflow({
  candidate,
  event,
  index,
  maxValue,
  valueOverflow,
  spinButtonRefs,
  onTrailingOverflow,
  onTrailingEdge,
}: {
  candidate: string;
  event: InputEvent;
  index: number;
  maxValue: number;
  valueOverflow: boolean;
  spinButtonRefs: { current: SpinButtonHandler[] };
  onTrailingOverflow?: (overflowChar: string) => void;
  onTrailingEdge?: () => void;
}) {
  // Search for the next configured spinbutton within the group.
  const nextSpinButton = spinButtonRefs.current.find(
    (handler, i) => handler && i > index,
  );

  if (!valueOverflow || candidate === '') {
    return;
  }

  // Shared derived values used across all cases below.
  const currentHandler = spinButtonRefs.current[index] as
    | NumberSpinButtonRef
    | undefined;
  if (!currentHandler) {
    return;
  }

  const currentText = currentHandler?.element?.current?.textContent ?? '';
  const sel = currentHandler?.getSelection();
  const maxNrOfDigits = maxValue.toString().length;
  const overflowChar = candidate.substring(candidate.length - 1);
  const overflowDigit = parseInt(overflowChar);
  const isNumericOverflow = Number.isInteger(overflowDigit);
  const candidateValue = parseInt(candidate);

  const hasNextInGroup = nextSpinButton !== undefined;
  const canAdvanceOutOfGroup =
    nextSpinButton === undefined && onTrailingEdge !== undefined;
  const isReplacingWholeValue =
    sel !== undefined && sel.start === 0 && sel.end === currentText.length;

  // Auto-advance: impossible first digit.
  // Keep the digit in current and move focus to the next spinbutton.
  if (
    isNumericOverflow &&
    maxNrOfDigits >= 2 &&
    candidate.length === 1 &&
    candidateValue > Math.floor(maxValue / 10) &&
    (currentText === '' || isReplacingWholeValue) &&
    (hasNextInGroup || canAdvanceOutOfGroup)
  ) {
    currentHandler.applyText(candidate, true);
    event.preventDefault();

    if (hasNextInGroup) {
      nextSpinButton.element.current?.focus();
    } else {
      onTrailingEdge?.();
    }
    return;
  }

  // Auto-advance: complete value.
  // Commit current value and move focus to the next spinbutton.
  if (candidate.length === maxNrOfDigits && candidateValue <= maxValue) {
    const hasCrossGroupEdge = !hasNextInGroup && onTrailingEdge !== undefined;

    if (hasNextInGroup || hasCrossGroupEdge) {
      // Commit the complete value manually so we can preventDefault.
      currentHandler?.applyText(candidate, true);
      event.preventDefault();
      if (hasNextInGroup) {
        nextSpinButton.element.current?.focus();
      } else {
        onTrailingEdge?.();
      }
      return;
    }

    // Last spinbutton with no advance target: commit and select all so the
    // field remains interactive — the next keystroke replaces the value.
    currentHandler.applyText(candidate, true);
    event.preventDefault();
    currentHandler.selectAll();
    return;
  }

  // Auto-advance: value overflow — candidate fills max digits but exceeds maxValue.
  // Forward the overflow char to the next spinbutton without committing the invalid candidate.
  if (
    isNumericOverflow &&
    candidate.length === maxNrOfDigits &&
    candidateValue > maxValue &&
    hasNextInGroup
  ) {
    event.preventDefault();
    if (nextSpinButton.type === 'number') {
      nextSpinButton.applyText(overflowChar, true);
      nextSpinButton.element.current?.focus();
      nextSpinButton.setSelection({ start: 0, end: 0, rtl: true });
    } else {
      nextSpinButton.searchValue(overflowChar, true);
      nextSpinButton.element.current?.focus();
    }
    return;
  }

  if (!hasNextInGroup && onTrailingOverflow) {
    // No next spin button in this group - check if overflow would
    // occur and forward the overflow character to the external handler.
    if (!isNumericOverflow) {
      return;
    }
    // Nothing to do if the candidate value fits within the current spin button.
    if (candidate.length <= maxNrOfDigits && candidateValue <= maxValue) {
      return;
    }
    event.preventDefault();
    onTrailingOverflow(overflowChar);
  }
}

/**
 * Hook to provide ootb value overflow behavior for spinbuttons within the group.
 * To achieve this, the provided children needs to be cloned to get access to the imperative handlers.
 * In addition to the spin button handlers, it provides an array of trigger refs.
 */
export function useValueOverflow(
  children: ReactNode,
  valueOverflow: boolean,
  onTrailingOverflow?: (overflowChar: string) => void,
  onTrailingEdge?: () => void,
) {
  const spinButtonRefs = useRef<SpinButtonHandler[]>([]);
  const triggerRefs = useRef<string[]>([]);

  // Extract, store and link potential external ref to the current ref.
  function linkRefAndRemember<T extends SpinButtonHandler>(
    child: ReactNode,
    index: number,
    handler: T,
  ) {
    // Remember component handler for ffu.
    spinButtonRefs.current[index] = handler;
    const { ref } = child as ChildWithRef<T>;

    // Nothing else to do if no external ref was passed.
    if (ref === undefined || ref === null) {
      return;
    }

    // Execute callback ref or forward handler to external ref.
    if (typeof ref === 'function') {
      ref(handler);
    } else {
      ref.current = handler;
    }
  }

  // Cloned children with extended ootb functionality
  const clonedChildren = useMemo(
    () =>
      Children.map(children, (child, index) => {
        if (!isValidElement(child)) {
          return child;
        }

        if (isNumberSpinButton(child)) {
          return cloneElement<
            NumberSpinButtonProps & {
              ref?: (handler: NumberSpinButtonRef) => void;
            }
          >(
            child,
            {
              ...child.props,
              ref: (handler: NumberSpinButtonRef) =>
                linkRefAndRemember(child, index, handler),
              onBeforeInput: (candidate, event) => {
                const {
                  maxValue = NUMBER_SPIN_BUTTON_DEFAULTS.MAX_VALUE,
                  onBeforeInput,
                } = child.props as NumberSpinButtonProps;

                // Call an external handler if one is set, exit early if necessary.
                onBeforeInput?.(candidate, event);
                if (event.defaultPrevented) {
                  return;
                }

                handleValueOverflow({
                  candidate,
                  event,
                  index,
                  maxValue,
                  valueOverflow,
                  spinButtonRefs,
                  onTrailingOverflow,
                  onTrailingEdge,
                });
              },
            },
            children,
          );
        } else if (isTextSpinButton(child)) {
          return cloneElement<
            TextSpinButtonProps & { ref?: (handler: TextSpinButtonRef) => void }
          >(
            child,
            {
              ...child.props,
              ref: (handler: TextSpinButtonRef) =>
                linkRefAndRemember(child, index, handler),
            },
            children,
          );
        } else if (isSpinButtonGroupSeparator(child)) {
          // Remember the trigger key for the current child index
          triggerRefs.current[index] = (
            child.props as SpinButtonGroupSeparatorProps
          ).trigger;
          return child;
        }

        return child;
      }),
    [children, valueOverflow, onTrailingOverflow, onTrailingEdge],
  );

  return {
    clonedChildren,
    spinButtonRefs,
    triggerRefs,
  };
}
